import { FC, ReactNode, Suspense, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router-dom";
import {
  Asset,
  Balance,
  BalanceAccount,
  GAIA,
  Msg,
  MsgExecute,
  MsgRedactedDepositToSubWallet,
  MsgRedactedWithdraw,
  MsgRedactedWithdrawFromSubWallet,
  MsgSecureDeposit,
  MsgSwap,
  MsgSwitch,
  THOR,
  isThor,
  signers,
  validateAddress,
} from "rujira.js";
import {
  AssetOverviewOption,
  Button,
  DepositModal as BaseDepositModal,
  Icons,
  Input,
  trim0x,
  useGlobalModalContext,
  useLocale,
  sortByValueUsd,
  useTranslation,
} from "rujira.ui";
import "rujira.ui/scss/index.scss";
import { useWalletAccounts } from "../../services/accounts";
import { isSunsetSymbol } from "../../services/asset";
import {
  canonicalAssetSymbol,
  canonicalChainFor,
  isSameCanonicalAsset,
} from "../../services/canonicalAsset";
import { QUERY_CLIENT } from "../../services/queryClient";
import { findAssetByUrlSegment } from "../../services/assetUrl";
import {
  canonicalDepositAssets,
  compareDepositAssetPriority,
  isDirectRujiraDepositAsset,
  isDirectRujiraDepositSymbol,
  isSupportedDepositNetwork,
} from "../../services/depositAssets";
import { Send } from "../../portfolio/components/actions/Send";
import { DepositQuery } from "./__generated__/DepositQuery.graphql";
import {
  BalanceProvider,
  useActiveBalances,
  usePreloadedBalance,
  usePreloadedBalances,
  usePreloadedMyProxyBalances,
  usePreloadedSubBalances,
} from "./Balance";
import { DepositDetail, type DepositDetailSubmitProps } from "./DepositDetail";
import { MsgProvider, TxButton } from "./TxButton";
import { BanxaOnRamp } from "./BanxaOnRamp";
import { TransferFooter } from "./TransferModal";
import { TransferSession } from "./TransferSession";
import { TransferSuccess } from "./TransferSuccess";
import { ExchangeDeposit } from "./ExchangeDeposit";
import { useRedacted } from "../../services/redacted";
import { useRedactedV2 } from "../../redacted/v2";
import { REDACTED_PROXY_CONTRACT } from "../../redacted/config";
import { useGetEncStealth } from "../../redacted";
import { Id } from "../../node";
import { balanceQueryAddresses } from "../../services/balanceAddresses";
import {
  privateAssetFlow,
  privateAssetSource,
  privateFlowSourceAccounts,
  type PrivateAssetFlow,
} from "./PrivateAssets.logic";

const query = graphql`
  query DepositQuery($publicId: ID, $publicAddresses: [Address!]!) {
    # Private Mode deposits from the connected wallet: its own public balances,
    # never the private account's (the product pages' balance context).
    node(id: $publicId) {
      ... on Account {
        balancesV2(first: 100, addresses: $publicAddresses) {
          edges {
            node {
              id
              balance
              valueUsd
              asset {
                asset
                type
                chain
                price {
                  current
                }
                metadata {
                  symbol
                  decimals
                }
                variants {
                  native {
                    denom
                  }
                }
              }
              accounts {
                address
                chain
                balance
                valueUsd
              }
            }
          }
        }
      }
    }
    thorchainV2 @catch(to: NULL) {
      rune {
        asset
        type
        chain
        variants {
          native {
            denom
          }
        }
        price {
          current
        }
        metadata {
          symbol
          decimals
        }
      }
      pools {
        status
        asset {
          asset
          type
          chain
          price {
            current
          }
          metadata {
            symbol
            decimals
          }
        }
      }
      inboundAddresses {
        dustThreshold
        chain
      }
    }
    staking @catch(to: NULL) {
      pools {
        bondAsset {
          asset
          type
          chain
          variants {
            native {
              denom
            }
          }
          price {
            current
          }
          metadata {
            symbol
            decimals
          }
        }
      }
    }
  }
`;

type DepositProps = {
  amount?: bigint;
  label?: string | ReactNode;
  dismiss?: () => void;
  assetSegment?: string;
  /** Private Mode: move between the Private Reserve and Spending. */
  isSubWallet?: boolean;
  /** Private Mode: start in the withdrawal direction. */
  isWithdraw?: boolean;
};

export const Deposit: FC<DepositProps> = ({
  amount,
  label,
  dismiss,
  assetSegment,
  isSubWallet,
  isWithdraw,
}) => (
  <TransferSession>
    {(connect) => (
      <Suspense fallback={<Fallback />}>
        <Content
          amount={amount}
          label={label}
          dismiss={dismiss}
          assetSegment={assetSegment}
          connect={connect}
          isSubWallet={isSubWallet}
          isWithdraw={isWithdraw}
        />
      </Suspense>
    )}
  </TransferSession>
);

const Content: FC<DepositProps & { connect: () => void }> = (props) => {
  const { isPrivateMode } = useRedacted();
  // Private Mode keeps its own source and destination rules; the public
  // transfer flows (detail step, exchange, fiat on-ramp) never open there.
  return isPrivateMode ? (
    <PrivateContent {...props} />
  ) : (
    <PublicContent {...props} />
  );
};

const PublicContent: FC<DepositProps & { connect: () => void }> = ({
  amount,
  label,
  dismiss,
  assetSegment,
  connect,
}) => {
  const [mode, setMode] = useState<"deposit" | "banxa">("deposit");
  const { balance } = usePreloadedBalance();
  const { hideModal } = useGlobalModalContext();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { toRoot } = useLocale();
  const accountProvider = useWalletAccounts();
  const hasConnectedWallet = Boolean(accountProvider.accounts?.length);
  const [exchangeFlow, setExchangeFlow] = useState(false);
  const q = useLazyLoadQuery<DepositQuery>(query, {
    publicId: undefined,
    publicAddresses: [],
  });
  const balances = usePreloadedBalances();
  const pools = q.thorchainV2?.pools;
  const poolAssets = useMemo(
    () => pools?.map((pool) => pool.asset) || [],
    [pools]
  );
  const inboundAddresses = q.thorchainV2?.inboundAddresses
    ? [...q.thorchainV2.inboundAddresses]
    : undefined;
  const directRujiraAssets = useMemo(
    () =>
      canonicalDepositAssets(
        [
          q.thorchainV2?.rune,
          ...poolAssets,
          ...(q.staking?.pools || []).map((pool) => pool.bondAsset),
        ].flatMap((asset): Asset[] =>
          asset && isDirectRujiraDepositSymbol(asset.metadata.symbol)
            ? [asset]
            : []
        )
      ),
    [poolAssets, q.staking?.pools, q.thorchainV2?.rune]
  );
  const exchangeAssets = useMemo(
    () =>
      canonicalDepositAssets([
        ...poolAssets.filter(
          (asset) =>
            asset.type !== "NATIVE" &&
            isSupportedDepositNetwork(asset) &&
            !isSunsetSymbol(asset.metadata.symbol) &&
            asset.metadata.symbol.toUpperCase() !== "BRUNE"
        ),
        ...directRujiraAssets,
      ]),
    [directRujiraAssets, poolAssets]
  );
  const balanceOptions = useMemo(
    () =>
      sortByValueUsd(
        groupDepositAccounts(balances || []).map(([symbol, accounts]) => {
          const canonicalChain = canonicalChainFor(symbol);
          // Prefer the asset whose own symbol is the canonical one, so holding
          // a variant (WBTC, cbBTC, ...) never changes which asset is deposited.
          const asset =
            balances.find(
              (candidate) =>
                candidate.asset.metadata.symbol.toUpperCase() === symbol &&
                candidate.asset.type === "SECURED"
            )?.asset ||
            balances.find(
              (candidate) =>
                canonicalAssetSymbol(candidate.asset.metadata.symbol) ===
                  symbol && candidate.asset.type === "SECURED"
            )?.asset ||
            balances.find(
              (candidate) =>
                canonicalAssetSymbol(candidate.asset.metadata.symbol) === symbol
            )?.asset ||
            poolAssets.find(
              (candidate) =>
                candidate.metadata.symbol.toUpperCase() === symbol &&
                candidate.chain === canonicalChain
            ) ||
            poolAssets.find(
              (candidate) =>
                canonicalAssetSymbol(candidate.metadata.symbol) === symbol &&
                candidate.chain === canonicalChain
            ) ||
            accounts[0].asset;
          const balance: Balance = {
            asset,
            accounts,
            balance: accounts.reduce(
              (total, account) => total + account.balance,
              0n
            ),
            valueUsd: accounts.reduce(
              (total, account) => total + account.valueUsd,
              0n
            ),
          };
          return {
            asset,
            balance,
            amount: balance.balance,
            valueUsd: balance.valueUsd,
            featured: true,
            networks: [
              ...new Set(
                accounts.map((account) =>
                  account.asset.type === "SECURED" ? THOR : account.asset.chain
                )
              ),
            ],
          };
        })
      ),
    [balances, poolAssets]
  );
  const directOptions = useMemo(
    () =>
      directRujiraAssets.map((asset) => {
        const accounts: BalanceAccount[] = [];
        const balance: Balance = {
          asset,
          accounts,
          balance: 0n,
          valueUsd: 0n,
        };
        return {
          asset,
          balance,
          amount: undefined,
          valueUsd: undefined,
          featured: false,
          networks: [THOR],
          secondary: (
            <span className="tag tag--md">
              {t("manualDepositAvailableNow")}
            </span>
          ),
        };
      }),
    [directRujiraAssets, t]
  );
  const selected =
    balance && !isSunsetSymbol(balance.asset.metadata.symbol)
      ? filterBalance(balance)
      : undefined;
  const overviewBalanceOptions = useMemo(
    () => sortByValueUsd([...balanceOptions, ...directOptions]),
    [balanceOptions, directOptions]
  );
  const options = useMemo(
    () =>
      pools
        ? canonicalDepositAssets([...poolAssets, ...directRujiraAssets])
            .sort(compareDepositAssetPriority)
            .reduce(
              (a: (AssetOverviewOption & { balance?: Balance })[], v) =>
                (v.type === "NATIVE" && !isDirectRujiraDepositAsset(v)) ||
                isSunsetSymbol(v.metadata.symbol) ||
                v.metadata.symbol.toUpperCase() === "BRUNE"
                  ? a
                  : a.find((x) =>
                        isSameCanonicalAsset(
                          x.asset.metadata.symbol,
                          v.metadata.symbol
                        )
                      )
                    ? a
                    : [
                        ...a,
                        {
                          asset: v,
                          secondary: hasConnectedWallet ? (
                            <span className="tag tag--md">
                              {t("manualDepositAvailable")}
                            </span>
                          ) : undefined,
                          // The current detail step requires a source BalanceAccount.
                          // Keep manual-only assets blocked until that flow exists.
                          disabled: true,
                        },
                      ],
              overviewBalanceOptions
            )
        : overviewBalanceOptions,
    [
      overviewBalanceOptions,
      hasConnectedWallet,
      poolAssets,
      directRujiraAssets,
      pools,
      t,
    ]
  );

  // Keep every hook above this: an early return can't sit ahead of one.
  if (mode === "banxa") {
    return <BanxaOnRamp onBack={() => setMode("deposit")} />;
  }

  const initialAsset = assetSegment
    ? findAssetByUrlSegment(
        canonicalAssetSymbol(assetSegment),
        options.flatMap((option) =>
          "disabled" in option && option.disabled ? [] : [option.asset]
        )
      )
    : undefined;
  const close = dismiss || hideModal;
  const targets =
    accountProvider.accounts
      ?.filter((account) => isThor(account.address))
      .map((account) => account.address) || [];
  if (exchangeFlow) {
    return (
      <ExchangeDeposit
        assets={exchangeAssets}
        balances={balances || []}
        connectedAccounts={accountProvider.accounts || []}
        targets={targets}
        target={accountProvider.selected?.address}
        Submit={Submit}
        dismiss={close}
        back={() => setExchangeFlow(false)}
        connect={connect}
        queryClient={QUERY_CLIENT}
        inboundAddresses={inboundAddresses}
      />
    );
  }
  return (
    <BaseDepositModal
      isPrivateMode={false}
      selected={selected}
      initialAsset={initialAsset}
      amount={amount}
      options={options}
      overview={{
        title: t("depositAssetsTitle"),
        description: hasConnectedWallet
          ? t("depositAssetsDescription")
          : t("depositAssetsDisconnectedDescription"),
        searchPlaceholder: t("searchAssets"),
        toggleLabel: hasConnectedWallet
          ? t("showAllAssets")
          : t("showAllSupportedAssets"),
        initialShowAll: !hasConnectedWallet,
        emptyLabel: t("noBalancesFound"),
        optionsFooter: (
          <span>
            {t("cantFindYourAsset")}{" "}
            <a href="https://rujira.network/faq/deposit" className="link">
              {t("learnMore")}
            </a>
          </span>
        ),
        actions: hasConnectedWallet
          ? [
              ...(__BANXA_ENABLED__
                ? [
                    {
                      title: t("buyCryptoWithFiat"),
                      description: t("banxaEntryCaption"),
                      icon: <Icons.CreditCard />,
                      onClick: () => setMode("banxa"),
                    },
                  ]
                : []),
              {
                title: t("connectAnotherWallet"),
                description: t("connectAnotherWalletDescription"),
                icon: <Icons.Wallet />,
                onClick: () => {
                  hideModal();
                  navigate(toRoot("connect"));
                },
              },
              {
                title: t("manualDeposit"),
                description: t("manualDepositEntryDescription"),
                icon: <Icons.Deposit />,
                onClick: () => setExchangeFlow(true),
              },
              {
                title: t("learnAboutDeposits"),
                description: t("learnAboutDepositsDescription"),
                icon: <Icons.Book />,
                href: "/how-to-deposit",
              },
            ]
          : [
              {
                title: t("connectYourWallet"),
                description: t("connectYourWalletDescription"),
                icon: <Icons.Wallet />,
                onClick: () => {
                  hideModal();
                  navigate(toRoot("connect"));
                },
              },
              {
                title: t("howDepositsWork"),
                description: t("howDepositsWorkDescription"),
                icon: <Icons.Book />,
                href: "/how-to-deposit",
              },
            ],
      }}
      Submit={Submit}
      dismiss={close}
      target={accountProvider.selected?.address}
      label={label}
      targets={targets}
      queryClient={QUERY_CLIENT}
      inboundAddresses={inboundAddresses}
      renderDetail={(props) =>
        isDirectRujiraDepositAsset(props.asset) ? (
          <BalanceProvider asset={props.asset}>
            <Send
              asset={props.asset}
              intent="deposit"
              cancel={props.cancel}
              connect={connect}
              onBack={props.onBack}
            />
          </BalanceProvider>
        ) : (
          <DepositDetail
            {...props}
            amount={amount}
            Submit={Submit}
            connect={connect}
            queryClient={QUERY_CLIENT}
            inboundAddresses={inboundAddresses}
          />
        )
      }
      renderSuccess={(props) => <TransferSuccess action="deposit" {...props} />}
    />
  );
};

/**
 * Private Mode moves funds between the connected wallet, the Private Reserve
 * and Spending only. Sources come from the flow (public wallet, Reserve or
 * Spending), never from the private account's product balances, and nothing
 * here bridges, swaps or offers exchange or fiat deposits.
 */
const PrivateContent: FC<DepositProps & { connect: () => void }> = ({
  amount,
  label,
  dismiss,
  assetSegment,
  isSubWallet,
  isWithdraw,
}) => {
  const redacted = useRedacted();
  const v2 = useRedactedV2();
  const { t } = useTranslation("common");
  const [isDeposit, setIsDeposit] = useState(!isWithdraw);
  const { asset: requestedAsset } = usePreloadedBalance();
  const { hideModal } = useGlobalModalContext();
  const accountProvider = useWalletAccounts();
  const q = useLazyLoadQuery<DepositQuery>(query, {
    publicId: accountProvider.selected
      ? Id.account({ address: accountProvider.selected.address.address })
      : undefined,
    publicAddresses: balanceQueryAddresses(accountProvider.accounts),
  });
  const reserveBalances = usePreloadedMyProxyBalances();
  const spendingBalances = usePreloadedSubBalances();
  const privateFlow = privateAssetFlow(isDeposit, isSubWallet);
  const publicBalances = useMemo(
    () =>
      q.node?.balancesV2?.edges?.flatMap((edge) => {
        const node = edge?.node;
        if (!node) return [];
        return [
          {
            id: node.id,
            asset: node.asset as Asset,
            balance: node.balance,
            valueUsd: node.valueUsd,
            accounts: node.accounts.map(
              (account): BalanceAccount => ({
                address: account.address,
                chain: account.chain,
                balance: account.balance,
                valueUsd: account.valueUsd,
                asset: node.asset as Asset,
              })
            ),
          },
        ];
      }) ?? [],
    [q.node]
  );
  const privateSource = privateAssetSource(privateFlow);
  const sourceBalances =
    privateSource === "public"
      ? publicBalances
      : privateSource === "reserve"
        ? reserveBalances
        : spendingBalances;
  const balanceOptions = sortByValueUsd(
    [...sourceBalances]
      .map((a) => {
        const filtered = filterBalanceForPrivateMode(a, privateFlow);
        return {
          asset: a.asset,
          balance: filtered,
          amount: filtered.balance,
          valueUsd: filtered.valueUsd,
        };
      })
      .filter(
        (a) =>
          !isSunsetSymbol(a.asset.metadata.symbol) && a.balance.accounts.length
      )
  );

  const selectedSource = requestedAsset
    ? (balanceOptions.find(
        (option) => option.asset.asset === requestedAsset.asset
      )?.balance ?? {
        // Keep the requested product asset visible even when this source has
        // none. An empty balance has no signer and cannot create a message.
        asset: requestedAsset,
        balance: 0n,
        valueUsd: 0n,
        accounts: [],
      })
    : undefined;

  const options: (AssetOverviewOption & { balance?: Balance })[] =
    requestedAsset &&
    selectedSource &&
    !balanceOptions.some(
      (option) => option.asset.asset === requestedAsset.asset
    )
      ? [
          {
            asset: requestedAsset,
            balance: selectedSource,
            amount: 0n,
            valueUsd: 0n,
          },
          ...balanceOptions,
        ]
      : balanceOptions;
  const initialAsset =
    !requestedAsset && assetSegment
      ? findAssetByUrlSegment(
          canonicalAssetSymbol(assetSegment),
          options.map((option) => option.asset)
        )
      : undefined;

  return (
    <BaseDepositModal
      selected={selectedSource}
      initialAsset={initialAsset}
      amount={amount}
      options={options}
      overview={{
        // DepositModal names direction and destination itself in Private Mode.
        title: isDeposit ? t("deposit") : t("withdraw"),
        description: isSubWallet
          ? isDeposit
            ? t("redacted:privateOverviewToSpending")
            : t("redacted:privateOverviewToReserve")
          : isDeposit
            ? t("redacted:privateOverviewDeposit")
            : t("redacted:privateOverviewWithdraw"),
        searchPlaceholder: t("searchAssets"),
        emptyLabel: t("noBalancesFound"),
      }}
      Submit={Submit}
      dismiss={dismiss || hideModal}
      target={accountProvider.selected?.address}
      label={label}
      targets={
        accountProvider.accounts
          ?.filter((a) => isThor(a.address))
          .map((a) => a.address) || []
      }
      queryClient={QUERY_CLIENT}
      inboundAddresses={
        q.thorchainV2?.inboundAddresses
          ? [...q.thorchainV2.inboundAddresses]
          : undefined
      }
      isPrivateMode
      setIsDeposit={setIsDeposit}
      isDeposit={isDeposit}
      isSubWallet={isSubWallet}
      subWalletAddress={redacted.subWalletAddress || undefined}
      proxyAddress={v2.enabled ? v2.proxyContract : REDACTED_PROXY_CONTRACT}
      updateFlagData={redacted.updateRedactedFlagData}
    />
  );
};

type SubmitProps = DepositDetailSubmitProps & {
  /** Private Mode only: the direction of a Reserve/Spending/public move. */
  isDeposit?: boolean;
  isSubWallet?: boolean;
};

const Submit: FC<SubmitProps> = ({
  selected,
  target,
  amount,
  quote,
  onSuccess,
  cancel,
  disabled,
  isDangerous,
  shouldSwap,
  onSimulation,
  isDeposit = true,
  isSubWallet,
}) => {
  const { t } = useTranslation("common");
  const { hideModal } = useGlobalModalContext();
  const { isPrivateMode, stealth, publicAddress } = useRedacted();
  const v2 = useRedactedV2();
  const encStealthData = useGetEncStealth(v2.enabled ? null : stealth);
  const privateFlow = privateAssetFlow(isDeposit, isSubWallet);

  const msg = useMemo(() => {
    if (amount <= 0n || !selected || amount > selected.balance) return null;
    if (!target) return null;
    if (disabled) return null;

    if (!isPrivateMode) {
      return selected.asset.metadata.symbol === "LVN"
        ? msgSwitch(selected, amount, target)
        : shouldSwap
          ? typeof quote === "object" && "memo" in quote
            ? new MsgSwap(
                { address: selected.address, network: selected.asset.chain },
                selected.asset,
                amount,
                quote.memo
              )
            : null
          : new MsgSecureDeposit(
              { address: selected.address, network: selected.asset.chain },
              selected.asset,
              amount,
              target
            );
    }

    if (!stealth || !publicAddress) return null;
    const denom =
      selected.asset.variants?.native?.denom ||
      selected.asset.asset.toLowerCase();

    if (v2.enabled) {
      if (!v2.selectedSubwallet || v2.status !== "ready") return null;
      if (privateFlow === "deposit") {
        return v2.directDepositMessage({ denom, amount: amount.toString() });
      }
      if (privateFlow === "reserve-to-spending") {
        return new MsgRedactedDepositToSubWallet(
          { address: publicAddress, network: THOR },
          stealth,
          amount.toString(),
          denom
        );
      }
      if (privateFlow === "spending-to-reserve") {
        return new MsgRedactedWithdrawFromSubWallet(
          { address: publicAddress, network: THOR },
          stealth,
          amount.toString(),
          denom
        );
      }
      // A restored private identity can belong to a different public wallet.
      // Withdraw to the destination shown in this modal, not its key context.
      if (!validateAddress(THOR, target)) return null;
      return new MsgRedactedWithdraw(
        { address: target, network: THOR },
        stealth,
        amount.toString(),
        denom
      );
    }

    if (privateFlow === "deposit") {
      if (!encStealthData) return null;
      return new MsgExecute(
        { address: selected.address, network: THOR },
        signers.cosmos.coins(amount.toString(), denom),
        REDACTED_PROXY_CONTRACT,
        {
          deposit: {
            stealth: trim0x(encStealthData.stealth),
            t: encStealthData.t,
          },
        }
      );
    }
    if (privateFlow === "reserve-to-spending") {
      return new MsgRedactedDepositToSubWallet(
        { address: publicAddress, network: THOR },
        stealth,
        amount.toString(),
        denom
      );
    }
    if (privateFlow === "spending-to-reserve") {
      return new MsgRedactedWithdrawFromSubWallet(
        { address: publicAddress, network: THOR },
        stealth,
        amount.toString(),
        denom
      );
    }
    return new MsgRedactedWithdraw(
      { address: publicAddress, network: THOR },
      stealth,
      amount.toString(),
      denom
    );
  }, [
    amount,
    disabled,
    encStealthData,
    isPrivateMode,
    privateFlow,
    publicAddress,
    quote,
    selected,
    shouldSwap,
    stealth,
    target,
    v2,
  ]);

  if (isPrivateMode) {
    return (
      <MsgProvider msg={msg}>
        <div
          className="modal__footer mt-4 px-3 py-2 text-right"
          style={{ position: "relative" }}>
          <div style={{ position: "absolute" }}>
            <Button
              className="button--grey button--outline"
              onClick={cancel || hideModal}
              label={t("cancel")}
            />
          </div>
          <TxButton
            className={isDangerous ? "button--red ml-1" : "ml-1"}
            onSuccess={onSuccess}
            onSimulation={onSimulation}
            label={isDangerous ? t("iUnderstandContinue") : t("confirm")}
            submitPublic={!v2.enabled && isDeposit && !isSubWallet}
            disabled={disabled || !msg}
          />
        </div>
      </MsgProvider>
    );
  }

  if (!selected) return null;
  return (
    <MsgProvider msg={msg}>
      <TransferFooter
        cancel={cancel || hideModal}
        action={
          <TxButton
            className={isDangerous ? "button--red ml-1" : "ml-1"}
            onSuccess={onSuccess}
            onSimulation={onSimulation}
            assetSymbol={selected.asset.metadata.symbol}
            label={isDangerous ? t("iUnderstandContinue") : t("confirm")}
            disabled={disabled || !msg}
            hideSimulation
          />
        }
      />
    </MsgProvider>
  );
};

const msgSwitch = (b: BalanceAccount, amount: bigint, target: string): Msg => {
  const msg = new MsgSwitch(
    { address: b.address, network: GAIA },
    b.asset,
    amount,
    target
  );
  if (b.address.startsWith("cosmos")) return msg;
  throw new Error(`Invalid IBC Deposit for ${b.address}`);
};

const filterBalance = (v: {
  readonly accounts: ReadonlyArray<{
    readonly address: string;
    readonly asset: Asset;
    readonly balance: bigint;
    readonly valueUsd: bigint;
  }>;
  readonly asset: Asset;
  readonly balance: bigint;
  readonly valueUsd: bigint;
}): Balance => {
  const accounts = v.accounts.filter(
    (a) =>
      a.asset.type !== "SECURED" &&
      a.asset.chain !== THOR &&
      !isSunsetSymbol(a.asset.metadata.symbol)
  );

  const accountValue = accounts.reduce((a, v) => a + v.valueUsd, 0n);
  const accountBalance = accounts.reduce((a, v) => a + v.balance, 0n);
  return {
    asset: v.asset,
    valueUsd: accountValue,
    balance: accountBalance,
    accounts,
  };
};

const filterBalanceForPrivateMode = (
  v: {
    readonly accounts: ReadonlyArray<{
      readonly address: string;
      readonly chain?: string;
      readonly asset: Asset;
      readonly balance: bigint;
      readonly valueUsd: bigint;
    }>;
    readonly asset: Asset;
    readonly balance: bigint;
    readonly valueUsd: bigint;
  },
  flow: PrivateAssetFlow
): Balance => {
  const accounts = privateFlowSourceAccounts(flow, v.accounts);
  return {
    asset: v.asset,
    balance: accounts.reduce((total, account) => total + account.balance, 0n),
    valueUsd: accounts.reduce((total, account) => total + account.valueUsd, 0n),
    accounts,
  };
};

const groupDepositAccounts = (
  balances: readonly Parameters<typeof filterBalance>[0][]
): [string, BalanceAccount[]][] => {
  const groups = new Map<string, BalanceAccount[]>();
  for (const balance of balances) {
    for (const account of filterBalance(balance).accounts) {
      const symbol = canonicalAssetSymbol(account.asset.metadata.symbol);
      if (
        isSunsetSymbol(symbol) ||
        symbol === "BRUNE" ||
        isDirectRujiraDepositAsset(account.asset)
      )
        continue;
      const accounts = groups.get(symbol);
      if (
        accounts?.some(
          (candidate) =>
            candidate.address === account.address &&
            candidate.asset.asset === account.asset.asset
        )
      )
        continue;
      groups.set(symbol, [...(accounts || []), account]);
    }
  }
  return [...groups.entries()];
};

export const useDeposit = (
  amount: bigint,
  asset?: Asset,
  hint?: string | ReactNode
) => {
  const { showModal } = useGlobalModalContext();
  const { isPrivateMode } = useRedacted();
  const v2 = useRedactedV2();
  const reserveBalances = usePreloadedMyProxyBalances();
  const matchingReserve = reserveBalances.find(
    (balance) => balance.asset.asset === asset?.asset
  );
  const denom =
    asset?.variants?.native?.denom ??
    matchingReserve?.asset.variants?.native?.denom;
  const reserveAmount = !asset
    ? 0n
    : v2.enabled
      ? ((denom ? v2.reserveBalances.get(denom) : undefined) ?? 0n)
      : privateFlowSourceAccounts(
          "reserve-to-spending",
          matchingReserve?.accounts ?? []
        ).reduce((total, account) => total + account.balance, 0n);
  const fromReserve = isPrivateMode && reserveAmount > 0n;
  return () => {
    showModal({
      className: "modal--lg",
      backgroundClose: false,
      // Follows the current account, which connecting a wallet in it changes.
      keepOnAccountChange: true,
      children: asset ? (
        <BalanceProvider asset={asset}>
          <Deposit
            amount={amount}
            label={hint}
            isSubWallet={fromReserve || undefined}
          />
        </BalanceProvider>
      ) : (
        <Deposit amount={amount} label={hint} />
      ),
    });
  };
};

export const useDepositToSubWallet = (
  amount: bigint,
  asset?: Asset,
  hint?: string | ReactNode
) => {
  const { showModal } = useGlobalModalContext();
  return () =>
    showModal({
      backgroundClose: false,
      // Follows the current account, which connecting a wallet in it changes.
      keepOnAccountChange: true,
      children: asset ? (
        <BalanceProvider asset={asset}>
          <Deposit amount={amount} label={hint} isSubWallet />
        </BalanceProvider>
      ) : (
        <Deposit amount={amount} label={hint} isSubWallet />
      ),
    });
};

export const useWithdrawFromSubWallet = (
  amount: bigint,
  asset?: Asset,
  hint?: string | ReactNode
) => {
  const { showModal } = useGlobalModalContext();
  return () =>
    showModal({
      backgroundClose: false,
      // Follows the current account, which connecting a wallet in it changes.
      keepOnAccountChange: true,
      children: asset ? (
        <BalanceProvider asset={asset}>
          <Deposit amount={amount} label={hint} isSubWallet isWithdraw />
        </BalanceProvider>
      ) : (
        <Deposit amount={amount} label={hint} isSubWallet isWithdraw />
      ),
    });
};

export const useWithdrawToPublicAccount = (
  amount: bigint,
  asset?: Asset,
  hint?: string | ReactNode
) => {
  const { showModal } = useGlobalModalContext();
  return () =>
    showModal({
      backgroundClose: false,
      // Follows the current account, which connecting a wallet in it changes.
      keepOnAccountChange: true,
      children: asset ? (
        <BalanceProvider asset={asset}>
          <Deposit amount={amount} label={hint} isWithdraw />
        </BalanceProvider>
      ) : (
        <Deposit amount={amount} label={hint} isWithdraw />
      ),
    });
};

/**
 *
 * @param v
 * @returns Returns a spendiable BalanceAccount for the provided balance.asset.asset, or a function to open deposit modal
 */
export const useBalanceAccount = (v: {
  asset: { asset: string };
  amount: bigint;
}): BalanceAccount | null => {
  const balances = useActiveBalances();
  const balance = balances?.find((a) => a.asset.asset === v.asset.asset);
  const thorAccount = balance?.accounts.find(
    (a) => a.asset.asset === balance.asset.asset && a.chain === THOR
  );

  const required = v.amount - (thorAccount?.balance || 0n);
  return !thorAccount || required > 0n ? null : thorAccount;
};

const Fallback: FC = () => {
  const { t } = useTranslation("common");
  return (
    <div className="deposit-modal">
      <h3 className="h3">{t("deposit")}</h3>
      <Input label={t("search")} disabled={true} />
      <div className="deposit-modal__content row wrap pad-mini mt-1.5 gap-y-1">
        {[...Array(8)].map((_, i) => (
          <div className="col-6" key={i}>
            <div className="skeleton h-8 br-2"></div>
          </div>
        ))}
      </div>
    </div>
  );
};
